import { useEffect } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Check } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';

import { AuthCard } from '@/components/auth/shell/auth-card';
import { AuthSplitShell } from '@/components/auth/shell/auth-split-shell';
import { AuthTopLink } from '@/components/auth/shell/auth-top-link';
import { SignupForm } from '@/components/auth/signup/signup-form';
import { SocialSignIn } from '@/components/auth/shared/social-sign-in';
import { signupContent } from '@/components/auth/signup/signup-copy';
import { SignupKindChoice } from '@/components/auth/signup/signup-kind-choice';
import { useSignupKind } from '@/components/auth/signup/use-signup-kind';
import { ROUTES } from '@/constants/routes';
import { useTrialOffer } from '@/hooks/data/use-billing';
import {
  trackSignUpFormSubmit,
  trackSignUpFormView,
} from '@/lib/funnel-events';
import { isMetaInAppBrowser } from '@/lib/in-app-browser';
import { loadNamespaces } from '@/lib/i18n/i18n';
import { AUTH_HEADLINE } from '@/components/ui/typography';

const signupSearchSchema = z.object({
  token: z.string().optional(),
  // A link can open on the agency choice; any other value opens on the default.
  kind: z.string().optional(),
});

export const Route = createFileRoute('/_public/signup')({
  loader: () => loadNamespaces('auth'),
  validateSearch: signupSearchSchema,
  staticData: { title: 'route.signup.title' },
  component: SignupPage,
});

const TopBar = () => {
  const { t } = useTranslation('auth');

  return (
    <>
      <AuthTopLink to={ROUTES.HOME}>{t('shell.back_home')}</AuthTopLink>
      <span>
        <Trans
          i18nKey="signup.top_login"
          ns="auth"
          components={{ a: <AuthTopLink to={ROUTES.LOGIN} /> }}
        />
      </span>
    </>
  );
};

function SignupPage() {
  const { t } = useTranslation('auth');
  const { token, kind } = Route.useSearch();
  const { data: trialOffer } = useTrialOffer();
  const signupKind = useSignupKind(Boolean(token), kind);
  useEffect(() => trackSignUpFormView(isMetaInAppBrowser()), []);
  const content = signupContent(t, {
    offer: trialOffer,
    invited: Boolean(token),
  });

  return (
    <AuthSplitShell topBar={<TopBar />}>
      <h1 className={AUTH_HEADLINE}>{content.headline}</h1>

      <AuthCard>
        <div className="flex flex-col gap-4">
          {signupKind.offered && (
            <SignupKindChoice
              value={signupKind.chosen}
              onChange={signupKind.choose}
            />
          )}
          <SocialSignIn
            invitationToken={token}
            organizationKind={signupKind.sent}
            onLeave={trackSignUpFormSubmit}
          />
          <div className="flex flex-col gap-3">
            <SignupForm organizationKind={signupKind.sent} />
            <p className="text-success-700 text-fs-12 flex items-center justify-center gap-1.5 font-medium">
              <Check aria-hidden className="size-3.5" strokeWidth={2.5} />
              {content.noCard}
            </p>
          </div>
        </div>
      </AuthCard>

      {content.joinHint && (
        <p className="text-fg-3 text-fs-13 text-center">{content.joinHint}</p>
      )}
    </AuthSplitShell>
  );
}
